import { Component, inject, Input } from '@angular/core';
import { FormGroup, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { ShopService } from '@app/services/shops/shop.service';
import { CustomCodeFormShape, CustomCodeVariableDoc } from '../../custom-code/custom-code.model';
import { GlobalSettingsFormShape } from '../../../components/smart-cart-form-global-settings/global-settings.model';
import { createRuleGroupFormGroup, DEFAULT_RULE_GROUP } from './product-selection.model';

/**
 * Product Selection tab for the Upsell Items widget. Owns the rule-group FormArray (add / remove /
 * duplicate) and composes the per-group cards (`smart-cart-upsell-rule-group`) + the appearance
 * section (`smart-cart-upsell-appearance`) + the shared custom-code card. Binds to the widget's
 * `productSelection` FormGroup (`{ ruleGroups: FormArray, style, settings }`).
 */
@Component({
  selector: 'smart-cart-upsell-product-selection',
  templateUrl: './product-selection.component.html',
  styleUrls: ['./product-selection.component.scss'],
  standalone: false,
})
export class SmartCartUpsellProductSelectionComponent {
  @Input({ required: true }) formGroup!: UntypedFormGroup;
  /** Global Button Design settings — the per-widget button colors inherit these until overridden. */
  @Input({ required: true }) globalSettingsFormGroup!: FormGroup<GlobalSettingsFormShape>;
  // Defaults to the shop's real currency symbol (from ShopService, same source checkout-app uses);
  // a parent may still override via binding. Falls back to '$' when the shop currency is unknown.
  @Input() shopCurrencySymbol: string = inject(ShopService).shopCurrencySymbol || '$';
  /** Widget-level Custom code group (`settings.customCode`), rendered as the last card in this tab. */
  @Input() customCodeGroup?: FormGroup<CustomCodeFormShape>;
  /** Starter template used as the "reset to default" baseline in the code editor dialog. */
  @Input() customCodeDefault = '';
  /** Variable reference docs shown in the code editor dialog. */
  @Input() customCodeVariables: CustomCodeVariableDoc[] = [];

  private readonly rulesDynamicFormService = inject(RulesDynamicFormService);

  get ruleGroupsArray(): UntypedFormArray {
    return this.formGroup.get('ruleGroups') as UntypedFormArray;
  }

  getRuleGroupFormGroup(index: number): UntypedFormGroup {
    return this.ruleGroupsArray.at(index) as UntypedFormGroup;
  }

  private getNextRuleGroupId(): number {
    const ids = this.ruleGroupsArray.value.map((group: { id: number }) => group.id);
    return ids.length > 0 ? Math.max(...ids) + 1 : 0;
  }

  addRuleGroup(): void {
    const id = this.getNextRuleGroupId();
    this.ruleGroupsArray.push(
      createRuleGroupFormGroup(id, { ...DEFAULT_RULE_GROUP, id }, this.rulesDynamicFormService),
    );
  }

  removeRuleGroup(index: number): void {
    if (this.ruleGroupsArray.length > 1) {
      this.ruleGroupsArray.removeAt(index);
    }
  }

  duplicateRuleGroup(index: number): void {
    const source = this.ruleGroupsArray.at(index) as UntypedFormGroup;
    const id = this.getNextRuleGroupId();
    // getRawValue() so any disabled controls in the source group are copied too.
    this.ruleGroupsArray.push(
      createRuleGroupFormGroup(id, { ...source.getRawValue(), id }, this.rulesDynamicFormService),
    );
  }
}
